import type { GeocodeV4Response, PointOfInterest } from "@be-miniapp/api"

import type { RideLocation } from "../stores/ride-data"

export type RideAddressType = "pick-up" | "drop-off"

export type RouteAfterDropoffComplete =
  | { type: "booking" }
  | { type: "confirm-address"; pickup: RideLocation }
  | { type: "focus-pickup" }

export function isRideLocationResolvable(loc?: RideLocation): boolean {
  if (!loc?.location?.latitude || !loc?.location?.longitude) {
    return false
  }

  return !!(loc.name || loc.address || loc.placeId)
}

export function resolveRouteAfterDropoffComplete(
  pickup: RideLocation | undefined
): RouteAfterDropoffComplete {
  if (pickup?.isConfirmed) {
    return { type: "booking" }
  }

  if (isRideLocationResolvable(pickup)) {
    return { type: "confirm-address", pickup: pickup! }
  }

  return { type: "focus-pickup" }
}

export function buildRideLocationFromPoint(
  point: PointOfInterest,
  type: RideAddressType
): RideLocation {
  return {
    type,
    name: point.address?.split(",")?.[0] ?? "",
    address: point.address ?? "",
    placeId: point.pid ?? "",
    location: {
      latitude: point.lat ?? 0,
      longitude: point.long ?? 0,
    },
    distance: "",
    metadata: "",
    isConfirmed: true,
  }
}

function pointFromRideLocation(loc: RideLocation): PointOfInterest {
  return {
    pid: loc.type,
    content: loc.address ?? "",
    address: loc.address ?? "",
    long: loc.location?.longitude ?? 0,
    lat: loc.location?.latitude ?? 0,
    is_nearby: true,
    source: "",
  }
}

function pointFromGeocode(currentPoint: GeocodeV4Response): PointOfInterest {
  return {
    pid: currentPoint.address,
    content: currentPoint.address,
    address: currentPoint.address,
    long: currentPoint.address_full.results[0].geometry.location.lng,
    lat: currentPoint.address_full.results[0].geometry.location.lat,
    is_nearby: true,
    source: "",
  }
}

export function normalizeRouteMapPoints(params: {
  points: PointOfInterest[]
  currentPoint: GeocodeV4Response | undefined
  center: { lat: number; lng: number } | undefined
  pickup: RideLocation | undefined
  dropoff: RideLocation | undefined
}): PointOfInterest[] {
  const { points, currentPoint, center, pickup, dropoff } = params

  const pointToAppend: PointOfInterest[] = []

  if (
    center &&
    pickup?.location?.latitude !== undefined &&
    pickup?.location?.longitude !== undefined &&
    center.lat === pickup.location.latitude &&
    center.lng === pickup.location.longitude
  ) {
    pointToAppend.push(pointFromRideLocation(pickup))
  }

  if (
    center &&
    dropoff?.location?.latitude !== undefined &&
    dropoff?.location?.longitude !== undefined &&
    center.lat === dropoff.location.latitude &&
    center.lng === dropoff.location.longitude
  ) {
    pointToAppend.push(pointFromRideLocation(dropoff))
  }

  if (currentPoint) {
    pointToAppend.push(pointFromGeocode(currentPoint))
  }

  const seen = new Set<string>()
  const merged = [...pointToAppend, ...points]
  const out: PointOfInterest[] = []

  for (const point of merged) {
    const address = point.address ?? ""
    if (!address.trim()) continue
    if (seen.has(address)) continue
    seen.add(address)
    out.push(point)
  }

  return out
}

export function buildRouteSelectionCommitPlan(params: {
  addressType: RideAddressType
  selectedPoint: PointOfInterest | undefined
  pickup: RideLocation | undefined
  dropoff: RideLocation | undefined
}): {
  nextPickup?: RideLocation
  nextDropoff?: RideLocation
  navigateTo:
    | { path: "/ride/booking"; state?: undefined }
    | {
        path: "/ride/dropoff_location"
        state: { focusOnField: RideAddressType }
      }
} | null {
  const { addressType, selectedPoint, pickup, dropoff } = params
  if (!selectedPoint?.address) return null

  if (addressType === "pick-up") {
    const nextPickup = buildRideLocationFromPoint(selectedPoint, "pick-up")
    const needsDropoff = !dropoff?.location?.latitude || !dropoff?.isConfirmed
    return {
      nextPickup,
      navigateTo: needsDropoff
        ? {
            path: "/ride/dropoff_location",
            state: { focusOnField: "drop-off" },
          }
        : { path: "/ride/booking" },
    }
  }

  const nextDropoff = buildRideLocationFromPoint(selectedPoint, "drop-off")
  const needsPickup = !pickup?.location?.latitude || !pickup?.isConfirmed
  return {
    nextDropoff,
    navigateTo: needsPickup
      ? { path: "/ride/dropoff_location", state: { focusOnField: "pick-up" } }
      : { path: "/ride/booking" },
  }
}
